import React, { Fragment, useEffect, useState } from "react";
import ToastContent from "../common/ToastContent";
import { Account } from "react-aad-msal";
import { connect } from "react-redux";
import { Delete } from "../../api/apiUtils";
import { ErrorResponse } from "../../models";
import { DefaultButton, mergeStyleSets, PrimaryButton } from "office-ui-fabric-react";
import { mergeStyles } from "@fluentui/react";
import { last6Digits } from "../../services/Helper";
import { State } from "../../store";
import { SofProcessor } from "../../services/SofProcessor";
import { toast } from "react-toastify";
import { useHistory } from "react-router";
import * as ActivityLogStore from "../../store/ActivityLogsStore";
import * as GeneralStore from "../../store/GeneralStore";
import * as OfflineDataStore from "../../store/OfflineDataStore";
import * as SofViewStore from "../../store/SofViewStore";
import * as TruckNominationStore from "../../store/TruckNominationStore";

type UndoResult = {
  nominationNo: string;
};
type Props = ActivityLogStore.ActivityLogsState &
  GeneralStore.GeneralState &
  TruckNominationStore.TruckNominationState &
  OfflineDataStore.OfflineDataState &
  typeof ActivityLogStore.actionCreators &
  typeof TruckNominationStore.actionCreators &
  typeof OfflineDataStore.actionCreators &
  typeof SofViewStore.actionCreators & {
    account: Account;
  };

const buttonBase = mergeStyles({
  padding: "9px 10px",
  fontSize: 16,
  fontWeight: 500,
  height: 42,
  border: "none",
  color: "#4c4c4c",
  selectors: {
    [":active"]: {
      backgroundColor: "#008cc0"
    }
  }
});
const buttonIcon = mergeStyles({
  padding: "9px 10px",
  fontSize: 16,
  fontWeight: 500,
  height: 40,
  width: 40,
  justifyContent: "center",
  border: "1px solid #D8DFE6",
  borderRadius: "2px",
  display: "flex",
  alignItems: "center",
  color: "#4c4c4c",
  selectors: {
    [":active"]: {
      backgroundColor: "#008cc0"
    }
  }
});
const classes = mergeStyleSets({
  header: {
    fontSize: 20,
    fontWeight: 500,
    lineHeight: 24,
        color: "#0F7F79",
    textAlign: "left"
  },
  body: {
    textAlign: "left",
    margin: "18px 0 38px 0"
  },
  footer: {
    textAlign: "right"
  },
  button: [
    buttonBase,
    {
      padding: "10px 16.5px",
      height: "auto"
    }
  ],
  modalButton: [
    buttonBase,
    {
      padding: "9px 20px"
    }
  ],
  primaryButton: [
    buttonBase,
    {
      backgroundColor: "#009FDF",
      color: "#FFFFFF",
      padding: "9px 20px"
    }
  ]
});

const UndoModal: React.FC<Props> = (props: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [nominationNo, setNominationNo] = useState("");
  const [message, setMessage] = useState("");
  const [nomMessage, setNomMessage] = useState("");
  const [disableUndoButton, setDisableButton] = useState(true);
  const [canUndo, setCanUndo] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isRejected, setIsRejected] = useState(false);
  const history = useHistory();

  const undoClick = () => {
    setShowModal(true);
  };

  const undoLastAction = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      let result: UndoResult | undefined = undefined;
      if (nominationNo === undefined) return;
      const offlineData = props.offlineData.find(
        (f) => f.lineHandlingNo.toLowerCase() === nominationNo.toLowerCase()
      );
      if (offlineData === undefined) {
        const response = await Delete(
          `api/v1.0/nomination/${nominationNo}/activities/undo?correlationId=${props.correlationId}&isExtended=${props.isTruckUndoExtendedAllowed}`
        );
        setShowModal(false);
        setIsLoading(false);
        if (!response.ok) {
          let errorMessage = (await response.json()) as ErrorResponse;
          toast.error(
            <ToastContent
              title="Error in Undo"
              content={errorMessage.message}
            />,
            {
              position: "bottom-right",
              autoClose: 5000,
              hideProgressBar: true,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined
            }
          );
          return;
        }
        result = (await response.json()) as UndoResult;
      } else {
        // result = await SofProcessor.undoAsync(nominationNo);
        // setShowModal(false);
        // setIsLoading(false);
        // if (offlineData) {
        //   const actions = offlineData.actions.filter(
        //     (f) => f.executionTime !== result.logTime
        //   );
        //   if (actions.length > 0) {
        //     const newOfflineData = {
        //       nominationNo,
        //       actions
        //     };
        //     props.updateOfflineData(newOfflineData);
        //   } else {
        //     props.removeOfflineData(lineHandlingNo);
        //   }
        // }
      }

      if (result) {
        props.getActivityLogsByNomination(result.nominationNo, () => {
          props.getTruckNomination(nominationNo, false, false);
        });
      }
    } catch (err) {}
  };

  useEffect(() => {
    if (props.truckNomination.result) {
      setNominationNo(props.truckNomination.result.nominationNo);

      setIsRejected(props.truckNomination.result.isRejected);
    }
  }, [props.truckNomination]);

  useEffect(() => {
    if (props.activityLogs.result) {
      const userActivities = props.activityLogs.result.filter(
        (f) =>
          (!props.isOffline && f.performedBy !== undefined) ||
          (props.isOffline && f.offline)
      );
      setDisableButton(userActivities.length === 0);
    }
  }, [props.activityLogs, props.isOffline]);

  useEffect(() => {
    const { activityLogs, account } = props;
    if (activityLogs.result && account && showModal) {
      const lastAction = activityLogs.result
        .sort((a, b) => b.id - a.id)
        .find((f) => f.performedBy === account.accountIdentifier);
      if (lastAction) {
        const hasActivitiesAfter = activityLogs.result.filter(
          (f) =>
            f.id > lastAction.id &&
            f.nominationNo === lastAction.nominationNo &&
            f.performedBy !== undefined &&
            f.performedBy !== account.accountIdentifier
        );
        if (hasActivitiesAfter.length > 0) {
          setCanUndo(false);
          setMessage("");
          setNomMessage("");
        } else {
          setCanUndo(true);
          setMessage(lastAction.description);
          if (lastAction.nominationNo) {
            setNomMessage(`Nomination ${last6Digits(lastAction.nominationNo)}`);
          }
        }
      } else {
        setCanUndo(false);
        setMessage("");
        setNomMessage("");
      }
    }
  }, [showModal]);

  return (
    <>
      {props.isTruckEditActivityAllowed && (
        <Fragment>
          {props.truckNomination &&
            props.truckNomination.result &&
            props.truckNomination.result.status < 2 && (
                      <div>
                          <div
                              style={{
                                  textAlign: 'center',
                                  display: 'flex',
                                  alignItems: 'center',
                                  flexDirection: 'column',
                              }}

                              onClick={undoClick}
                          >
                              <div style={{
                                  height: 48, width: 48, padding: 12,
                                  borderRadius: '50%',
                                  background: '#F3F4F6'
                              }} ><svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24"><path d="M259-200v-60h310q70 0 120.5-46.5T740-422q0-69-50.5-115.5T569-584H274l114 114-42 42-186-186 186-186 42 42-114 114h294q95 0 163.5 64T800-422q0 94-68.5 158T568-200H259Z" /></svg>
                              </div>
                          </div>
                          <div style={{ fontSize: 12, textAlign: "center" }}>Undo</div>
                      </div>
            )}
        </Fragment>
      )}
      {showModal && (
        <div className="modal-bg">
          <div className="modal-right" style={{ width: 400 }}>
            <form>
              <div className={classes.header}>Undo Activity</div>
              <div className={classes.body}>
                {canUndo && (
                  <span>
                    You are about to undo your last activity,&nbsp;
                    <b id="lbl-last-activity">{message}</b>
                    {nomMessage && (
                      <>
                        &nbsp;in <b id="lbl-last-nomination">{nomMessage}</b>
                      </>
                    )}
                    . Do you want to proceed?
                  </span>
                )}
                {!canUndo && (
                  <span>
                    You cannot perform undo at this moment. The last activity
                    was performed by other user.
                  </span>
                )}
              </div>
              <div className={classes.footer}>
                              {canUndo && (
                                  <PrimaryButton
                                      id="btn-submit-undo"
                                      type="submit" 
                    onClick={undoLastAction}
                                  disabled={isLoading}
                                  text="Proceed"
                              />
                  //<button
                  //  id="btn-submit-undo"
                  //  className={classes.primaryButton}
                  //  type="submit"
                  //  onClick={undoLastAction}
                  //  disabled={isLoading}
                  //>
                  //  Proceed
                  //</button>
                )}
                              &nbsp;
                              <DefaultButton
                                  id="btn-cancel-undo"
                                  onClick={() => setShowModal(false)}
                                  disabled={isLoading}
                                  text="Cancel"
                              />
                {/*<button*/}
                {/*  id="btn-cancel-undo"*/}
                {/*  className={classes.modalButton}*/}
                {/*  type="button"*/}
                {/*  onClick={() => setShowModal(false)}*/}
                {/*  disabled={isLoading}*/}
                {/*>*/}
                {/*  Cancel*/}
                {/*</button>*/}
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
export default connect(
  (state: State) => ({
    ...state.activityLogs,
    ...state.authentication,
    ...state.general,
    ...state.truckNomination,
    ...state.offlineData
  }),
  {
    ...ActivityLogStore.actionCreators,
    ...TruckNominationStore.actionCreators,
    ...OfflineDataStore.actionCreators,
    ...SofViewStore.actionCreators
  }
)(UndoModal as any);
